import React, { useState, useEffect } from 'react';
import { updateProfile, updateEmail, updatePassword } from 'firebase/auth';
import { doc, getDoc, updateDoc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../firebaseConfig';
import { deleteUserAccount } from '../lib/callables';
import { useTheme } from '../context/ThemeContext';
import './Profile.css';

const walletOptions = [
  { name: 'Lace', icon: '💳' },
  { name: 'Yoroi', icon: '💳' },
  { name: 'Eternl', icon: '💳' },
  { name: 'Nami', icon: '💳' },
];

export default function Profile() {
  const { colors } = useTheme();
  const [user, setUser] = useState(auth.currentUser);
  const [pixBalance, setPixBalance] = useState<number>(0);
  const [wallet, setWallet] = useState<string>('');
  const [username, setUsername] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);

  useEffect(() => {
    if (!user) return;

    const userRef = doc(db, 'users', user.uid);
    const unsubscribe = onSnapshot(userRef, async (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setWallet(data.walletAddress || '');
        setUsername(data.username || user.displayName || '');
        setEmail(user.email || '');

        // Calculate PIX balance
        const completedTasks = data?.completedTasks || {};
        let totalEarned = 0;
        
        Object.values(completedTasks).forEach((task: any) => {
          const submissions = Array.isArray(task.submissions) ? task.submissions : [task];
          submissions.forEach((sub: any) => {
            if (sub.status === 'approved') {
              const reward = Number(sub.reward) || Number(sub.rewardAmount) || 0;
              totalEarned += reward;
            }
          });
        });
        
        // Get withdrawals
        const withdrawalsRef = doc(db, 'users', user.uid, 'withdrawals', 'total');
        const withdrawalsSnap = await getDoc(withdrawalsRef);
        const totalWithdrawn = withdrawalsSnap.exists() ? (withdrawalsSnap.data().amount || 0) : 0;
        
        setPixBalance(totalEarned - totalWithdrawn);
      }
    });

    return () => unsubscribe();
  }, [user]);

  const handleConnectWallet = async (walletName: string) => {
    const mockAddress = 'Q8xs' + Math.random().toString(36).substring(2, 10) + 'sf9x';
    setWallet(mockAddress);
    
    if (user) {
      try {
        await updateDoc(doc(db, 'users', user.uid), {
          walletAddress: mockAddress,
          walletProvider: walletName,
        });
      } catch (error) {
        console.error('Error saving wallet:', error);
      }
    }
    
    setShowWalletModal(false);
  };

  const handleDisconnectWallet = async () => {
    setWallet('');
    
    if (user) {
      try {
        await updateDoc(doc(db, 'users', user.uid), {
          walletAddress: '',
          walletProvider: '',
        });
      } catch (error) {
        console.error('Error disconnecting wallet:', error);
      }
    }
    
    setShowDisconnectModal(false);
  };

  const handleSaveUsername = async () => {
    if (!user) return;
    setSaving(true);
    
    try {
      await updateProfile(user, { displayName: username });
      await updateDoc(doc(db, 'users', user.uid), { username });
      alert('Username updated successfully!');
    } catch (error: any) {
      alert('Failed to update username: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEmail = async () => {
    if (!user) return;
    setSaving(true);
    
    try {
      await updateEmail(user, email);
      await updateDoc(doc(db, 'users', user.uid), { email });
      alert('Email updated successfully!');
    } catch (error: any) {
      alert('Failed to update email: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!user) return;
    if (newPassword !== confirmPassword) {
      alert('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }
    
    setSaving(true);
    
    try {
      await updatePassword(user, newPassword);
      alert('Password updated successfully!');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      alert('Failed to update password: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    const confirmed = window.confirm(
      '⚠️ WARNING: This will permanently delete your account and mark your submissions as deleted.\n\n' +
      'This action cannot be undone. Are you absolutely sure?'
    );

    if (!confirmed) return;

    const doubleConfirm = window.confirm(
      'This is your last chance. Click OK to permanently delete your account.'
    );

    if (!doubleConfirm) return;

    setDeleting(true);
    try {
      await deleteUserAccount();
      alert('Account deleted successfully. You will be logged out.');
      window.location.href = '/login';
    } catch (error: any) {
      console.error('Error deleting account:', error);
      alert('Failed to delete account: ' + error.message);
    } finally {
      setDeleting(false);
    }
  };

  if (!user) {
    return <div>Loading...</div>;
  }

  return (
    <div className="profile-page" style={{ background: colors.background, color: colors.text }}>
      <div className="profile-container">
        <h1 style={{ color: colors.text }}>Profile</h1>

        {/* Wallet Section */}
        <div className="profile-section" style={{ background: colors.card, borderColor: colors.border }}>
          <h2 style={{ color: colors.text }}>Wallet</h2>
          {wallet ? (
            <div className="wallet-info">
              <div className="wallet-row">
                <span className="wallet-label" style={{ color: colors.text }}>Connected Wallet:</span>
                <span className="wallet-address" style={{ color: colors.primary }}>{wallet}</span>
              </div>
              <div className="wallet-row">
                <span className="wallet-label" style={{ color: colors.text }}>$PIX Balance:</span>
                <span className="wallet-balance" style={{ color: colors.primary, fontWeight: 'bold' }}>
                  {pixBalance.toFixed(0)}
                </span>
              </div>
              <div className="wallet-actions">
                <button
                  className="action-button secondary-button"
                  style={{ borderColor: colors.error, color: colors.error }}
                  onClick={() => setShowDisconnectModal(true)}
                >
                  Disconnect Wallet
                </button>
                <button
                  className="action-button primary-button"
                  style={{ background: colors.primary }}
                  onClick={() => alert('Withdraw functionality coming soon!')}
                >
                  Withdraw
                </button>
              </div>
            </div>
          ) : (
            <div className="wallet-connect">
              <p style={{ color: colors.text, marginBottom: '16px' }}>Connect wallet to receive rewards</p>
              <button
                className="action-button primary-button"
                style={{ background: colors.primary }}
                onClick={() => setShowWalletModal(true)}
              >
                Connect Wallet
              </button>
            </div>
          )}
        </div>

        {/* Account Settings Section */}
        <div className="profile-section" style={{ background: colors.card, borderColor: colors.border }}>
          <h2 style={{ color: colors.text }}>Account Settings</h2>
          
          <div className="form-group">
            <label style={{ color: colors.text }}>Username</label>
            <div className="input-with-button">
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                style={{ background: colors.background, color: colors.text, borderColor: colors.border }}
              />
              <button
                className="action-button primary-button"
                style={{ background: colors.primary }}
                onClick={handleSaveUsername}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Update'}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label style={{ color: colors.text }}>Email</label>
            <div className="input-with-button">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{ background: colors.background, color: colors.text, borderColor: colors.border }}
              />
              <button
                className="action-button primary-button"
                style={{ background: colors.primary }}
                onClick={handleSaveEmail}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Update'}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label style={{ color: colors.text }}>Change Password</label>
            <div className="password-inputs">
              <input
                type="password"
                placeholder="New Password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                style={{ background: colors.background, color: colors.text, borderColor: colors.border }}
              />
              <input
                type="password"
                placeholder="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                style={{ background: colors.background, color: colors.text, borderColor: colors.border }}
              />
              <button
                className="action-button primary-button"
                style={{ background: colors.primary }}
                onClick={handleChangePassword}
                disabled={saving || !newPassword || !confirmPassword}
              >
                {saving ? 'Saving...' : 'Update Password'}
              </button>
            </div>
          </div>
        </div>


        {/* Danger Zone */}
        <div className="danger-zone" style={{ borderColor: colors.error }}>
          <h3 style={{ color: colors.error }}>Danger Zone</h3>
          <p className="danger-warning" style={{ color: colors.text }}>
            Deleting your account will permanently remove your account and mark all your submissions as deleted.
            This action cannot be undone.
          </p>
          <button
            className="action-button delete-button"
            style={{ background: colors.error }}
            onClick={handleDeleteAccount}
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete My Account'}
          </button>
        </div>
      </div>

      {/* Wallet Options Modal */}
      {showWalletModal && (
        <div className="modal-overlay" onClick={() => setShowWalletModal(false)}>
          <div className="modal-box" style={{ background: colors.card }} onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title" style={{ color: colors.text }}>Select a Wallet</h3>
            {walletOptions.map(opt => (
              <button
                key={opt.name}
                className="wallet-connect-btn"
                style={{ borderColor: colors.primary, color: colors.primary }}
                onClick={() => handleConnectWallet(opt.name)}
              >
                {opt.icon} Connect {opt.name} Wallet
              </button>
            ))}
            <button
              className="action-button primary-button"
              style={{ background: colors.primary }}
              onClick={() => setShowWalletModal(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Wallet Disconnect Modal */}
      {showDisconnectModal && (
        <div className="modal-overlay" onClick={() => setShowDisconnectModal(false)}>
          <div className="modal-box" style={{ background: colors.card }} onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title" style={{ color: colors.text }}>
              Are you sure you want to disconnect your wallet?
            </h3>
            <div className="modal-btn-row">
              <button
                className="action-button primary-button"
                style={{ background: colors.primary }}
                onClick={handleDisconnectWallet}
              >
                Disconnect
              </button>
              <button
                className="action-button secondary-button"
                style={{ borderColor: colors.border, color: colors.text }}
                onClick={() => setShowDisconnectModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
